// Number-provisioning service — talks to sk-orchestrator's org phone-number
// routes. Search Plivo inventory, then claim either franchise (one number for
// the whole org, AI asks which branch) or per-branch (rings straight through).
import axiosInstance from '../../api/axiosInstance';
import {
  Org_Locations_Url,
  Org_Phone_Numbers_Url,
  Org_Available_Numbers_Url,
  Org_Claim_Franchise_Url,
  Org_Claim_Branch_Url,
  Prov_State_Url,
  Prov_Start_Url,
  Prov_Kyc_Url,
  Prov_Numbers_Url,
  Prov_Buy_Url,
} from '../../api/endpoints';

export interface OrgLocation { id: string; name: string; number?: string | null }
export interface AvailableNumber {
  numberSid: string; number: string; region: string | null; type: string | null;
  monthlyRental: string | null; voiceEnabled: boolean;
}
export interface ClaimedNumber {
  id: string; number: string; status: string; locationId: string | null;
  pending: boolean; pendingReason: string | null;
}

export async function listLocations(orgId: string): Promise<OrgLocation[]> {
  const { data } = await axiosInstance.get(Org_Locations_Url(orgId));
  return data?.data ?? [];
}

export async function listOrgNumbers(orgId: string): Promise<ClaimedNumber[]> {
  const { data } = await axiosInstance.get(Org_Phone_Numbers_Url(orgId));
  return data?.data ?? [];
}

export async function searchNumbers(
  orgId: string, params: { country?: string; type?: string; pattern?: string } = {},
): Promise<AvailableNumber[]> {
  const { data } = await axiosInstance.get(Org_Available_Numbers_Url(orgId), { params: { country: 'IN', ...params } });
  return data?.data ?? [];
}

// Franchise: one number for the whole org (locationId null). AI asks which branch.
export async function claimFranchiseNumber(orgId: string, numberSid: string): Promise<ClaimedNumber> {
  const { data } = await axiosInstance.post(Org_Claim_Franchise_Url(orgId), { numberSid });
  return data?.data;
}

// Per-branch: a number that rings straight through to one location.
export async function claimBranchNumber(orgId: string, locationId: string, numberSid: string): Promise<ClaimedNumber> {
  const { data } = await axiosInstance.post(Org_Claim_Branch_Url(orgId, locationId), { numberSid });
  return data?.data;
}

// ── Go-live journey (the real Plivo reseller flow): subaccount → KYC → buy ──
export type ProvStep =
  | 'NOT_STARTED' | 'SUBACCOUNT_READY' | 'KYC_SUBMITTED' | 'KYC_ACCEPTED'
  | 'NUMBER_BOUGHT' | 'LIVE';
export type KycState = 'NONE' | 'SUBMITTED' | 'ACCEPTED' | 'REJECTED';

export interface ProvState {
  step: ProvStep;
  mode: 'PLIVO' | 'LOCAL';
  kyc_status: KycState;
  business_name: string | null;
  gstin: string | null;
  chosen_number: string | null;
  can_buy: boolean;
  is_live: boolean;
}

export interface ProvNumber {
  number: string; region: string | null; type: string | null;
  monthlyRental: string | null; voiceEnabled: boolean; assignedToOtherOrg: boolean;
}

export async function getProvisioning(orgId: string): Promise<ProvState> {
  const { data } = await axiosInstance.get(Prov_State_Url(orgId));
  return data?.data;
}

export async function startProvisioning(orgId: string, locationId?: string | null): Promise<ProvState> {
  const { data } = await axiosInstance.post(Prov_Start_Url(orgId), locationId ? { location_id: locationId } : {});
  return data?.data;
}

export async function submitKyc(
  orgId: string,
  input: { businessName: string; gstin?: string; documents: Record<string, string> },
): Promise<ProvState> {
  const { data } = await axiosInstance.post(Prov_Kyc_Url(orgId), {
    business_name: input.businessName,
    ...(input.gstin ? { gstin: input.gstin } : {}),
    documents: input.documents,
  });
  return data?.data;
}

export async function pollKyc(orgId: string): Promise<ProvState> {
  const { data } = await axiosInstance.get(Prov_Kyc_Url(orgId));
  return data?.data;
}

export async function listProvNumbers(orgId: string, pattern?: string): Promise<ProvNumber[]> {
  const { data } = await axiosInstance.get(Prov_Numbers_Url(orgId), { params: pattern ? { pattern } : {} });
  return data?.data ?? [];
}

export async function buyProvNumber(orgId: string, number: string, locationId?: string | null): Promise<ProvState> {
  const { data } = await axiosInstance.post(Prov_Buy_Url(orgId), {
    number,
    ...(locationId ? { location_id: locationId } : {}),
  });
  return data?.data;
}
